import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import Loader from "../components/common/Loader";
import { ROUTES } from "../constants/routes";
import { AuthData, LogoutResponse } from "../types/auth";
import { User } from "../types/user";
import { getToken, logout } from "../utils/auth-token";
import { isPublicPath } from "../utils/routes";
import { getDefaultRole } from "../utils/role";

const TOKEN_REFRESH_BUFFER_MS = 30 * 1000;
const MIN_TOKEN_REFRESH_DELAY_MS = 1000;
const TOKEN_REFRESH_RETRY_MS = 15 * 1000;

const EMPTY_AUTH: AuthData = {
  id: "",
  token: "",
  roles: [],
  default_role: "",
};

interface AuthContextInterface {
  loading: boolean;
  auth?: AuthData | null;
  handleLogout: () => void;
  setAuth: (data?: AuthData) => void;
  setUser: (user: User) => void;
  refetchToken: () => void;
}

interface AuthContextProps {
  children?: ReactNode;
}

const AuthContext = createContext<AuthContextInterface>({
  auth: null,
  setAuth: () => {},
  handleLogout: () => {},
  loading: false,
  setUser: () => {},
  refetchToken: () => {},
});

export const AuthContextProvider = ({ children }: AuthContextProps) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [auth, setAuth] = useState<AuthData>(EMPTY_AUTH);
  const [loading, setLoading] = useState(true);

  // timeoutRef keeps a single scheduled refresh so we don't double-refresh tokens.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenExpiryRef = useRef<number | null>(null);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const authRef = useRef<AuthData>(EMPTY_AUTH);
  const refreshSessionRef = useRef<
    (
      organizationId?: string,
      options?: { showLoader?: boolean },
    ) => Promise<void>
  >(async () => {});

  const setUser = (user: User) => {
    setAuth((prev) => {
      const nextAuth = { ...prev, user };
      authRef.current = nextAuth;
      return nextAuth;
    });
  };

  const clearTokenRefreshTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const needsTokenRefresh = useCallback(() => {
    if (!tokenExpiryRef.current) return true;

    return tokenExpiryRef.current - Date.now() <= TOKEN_REFRESH_BUFFER_MS;
  }, []);

  const setAuthData = useCallback(
    (data?: AuthData) => {
      const authData = data
        ? { ...data, default_role: getDefaultRole(data.user?.user_roles || []) }
        : EMPTY_AUTH;

      authRef.current = authData;
      tokenExpiryRef.current = data?.session?.access_token_expires_at
        ? data.session.access_token_expires_at * 1000
        : null;
      setAuth(authData);

      if (!data && !isPublicPath(window.location.pathname)) {
        navigate(ROUTES.root, { replace: true });
      }
    },
    [navigate],
  );

  const scheduleRefreshRetry = useCallback(() => {
    clearTokenRefreshTimeout();

    timeoutRef.current = setTimeout(() => {
      void refreshSessionRef.current(undefined, { showLoader: false });
    }, TOKEN_REFRESH_RETRY_MS);
  }, [clearTokenRefreshTimeout]);

  const scheduleTokenRefresh = useCallback(
    (expiresAt?: number) => {
      clearTokenRefreshTimeout();

      if (!expiresAt) {
        tokenExpiryRef.current = null;
        return;
      }

      tokenExpiryRef.current = expiresAt * 1000;
      const refreshIn =
        tokenExpiryRef.current - Date.now() - TOKEN_REFRESH_BUFFER_MS;
      const delay = refreshIn > 0 ? refreshIn : MIN_TOKEN_REFRESH_DELAY_MS;

      timeoutRef.current = setTimeout(() => {
        void refreshSessionRef.current(undefined, { showLoader: false });
      }, delay);
    },
    [clearTokenRefreshTimeout],
  );

  const handleLogout = async () => {
    setLoading(true);
    const res: LogoutResponse = await logout();

    clearTokenRefreshTimeout();
    refreshPromiseRef.current = null;
    tokenExpiryRef.current = null;

    if (res?.data?.logout?.message) {
      toast({
        title: "Success",
        description: res.data.logout.message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setAuthData();
      navigate(ROUTES.root, { replace: true });
    } else if (res?.errors?.length) {
      toast({
        title: "Error",
        description: res.errors[0].message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    setLoading(false);
  };

  const fetchToken = useCallback(
    async (organizationId?: string, options?: { showLoader?: boolean }) => {
      const showLoader = options?.showLoader ?? true;

      if (refreshPromiseRef.current) {
        return refreshPromiseRef.current;
      }

      if (!showLoader && !needsTokenRefresh() && timeoutRef.current) {
        return;
      }

      const refreshOperation = (async () => {
        try {
          if (showLoader) {
            setLoading(true);
          }

          const data = await getToken(organizationId);
          setAuthData(data);
          scheduleTokenRefresh(data?.session?.access_token_expires_at);
        } catch (err) {
          console.error(err);
          clearTokenRefreshTimeout();

          if (err instanceof Error && err.message === "No session found") {
            setAuthData();
            return;
          }

          if (
            authRef.current.id &&
            authRef.current.token &&
            authRef.current.user
          ) {
            scheduleRefreshRetry();
            return;
          }

          setAuthData();
        } finally {
          refreshPromiseRef.current = null;
          if (showLoader) {
            setLoading(false);
          }
        }
      })();

      refreshPromiseRef.current = refreshOperation;
      return refreshOperation;
    },
    [
      clearTokenRefreshTimeout,
      needsTokenRefresh,
      scheduleRefreshRetry,
      scheduleTokenRefresh,
      setAuthData,
    ],
  );

  refreshSessionRef.current = fetchToken;

  useEffect(() => {
    void fetchToken();

    const handleSessionRecovery = () => {
      if (document.visibilityState === "hidden") return;

      if (
        !(authRef.current.id && authRef.current.token && authRef.current.user)
      ) {
        return;
      }

      if (needsTokenRefresh() || !timeoutRef.current) {
        void fetchToken(undefined, { showLoader: false });
      }
    };

    window.addEventListener("focus", handleSessionRecovery);
    document.addEventListener("visibilitychange", handleSessionRecovery);

    return () => {
      clearTokenRefreshTimeout();
      window.removeEventListener("focus", handleSessionRecovery);
      document.removeEventListener("visibilitychange", handleSessionRecovery);
    };
  }, [clearTokenRefreshTimeout, fetchToken, needsTokenRefresh]);

  if (loading) {
    return <Loader height="100vh" width="100vw" />;
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth: setAuthData,
        handleLogout,
        loading,
        setUser,
        refetchToken: fetchToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
